import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { decodeJWTPayload } from '../../utils/jwt';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component for admin pages
 * Checks if user is admin (all permissions)
 * No public admin routes - all admin access is dynamically determined
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.warn('[AdminRoute] No session, redirecting to signin');
          navigate('/signin');
          return;
        }

        // Extract JWT claims (is_admin is set by custom_access_token_hook)
        // The hook checks the admins table and sets is_admin claim
        const claims = session.access_token ? decodeJWTPayload(session.access_token) : null;
        if (!claims) {
          console.error('[AdminRoute] Failed to decode JWT token');
          navigate('/admin/dashboard');
          return;
        }
        const isAdmin = claims.is_admin === true;

        if (!isAdmin) {
          console.warn('[AdminRoute] User is not an admin, redirecting to admin dashboard');
          navigate('/admin/dashboard');
          return;
        }

        setIsAuthorized(true);
      } catch (err) {
        console.error('[AdminRoute] Error checking admin access:', err);
        navigate('/admin/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminAccess();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Will redirect
  }

  return children;
}

